import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { MyLocalStorage } from "../../utils/storage";
import UserManagement from "./UserManagement";
import TenantManagement from "./TenantManagement";
import SubscriptionManagement from "./SubscriptionManagement";
import BillingManagement from "./BillingManagement";
import ActivityDashboard from "./ActivityDashboard";
import EmailVerificationBanner from "../EmailVerificationBanner";
import EndSessionButton from "../EndSessionButton";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TimelineIcon from "@mui/icons-material/Timeline";
import "./AdminPanel.css";
import Header from "../Header";
import Footer from "../Footer";

type TabType = "users" | "tenants" | "subscriptions" | "billing" | "activity";

const AdminPanel: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSuperadmin = user?.is_superadmin ?? false;
  const isTenantAdmin = user?.role === "admin" && !isSuperadmin;
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);

  // Check if currently impersonating
  useEffect(() => {
    const checkImpersonation = () => {
      const adminToken = MyLocalStorage.jwt_token_admin.get();
      setIsImpersonating(!!adminToken);
    };

    checkImpersonation();
    const interval = setInterval(checkImpersonation, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Default tab: billing for tenant admins, users for superadmins
  const getDefaultTab = (): TabType => {
    // If returning from payment, go to billing
    const billingResult = searchParams.get("billing");
    if ((billingResult === "success" || billingResult === "canceled") && isTenantAdmin) {
      return "billing";
    }
    const tabParam = searchParams.get("tab");
    if (tabParam === "billing" && isTenantAdmin) return "billing";
    return "users";
  };
  
  const [activeTab, setActiveTab] = useState<TabType>(getDefaultTab);
  
  // Update tab when URL param changes (e.g., returning from Stripe)
  useEffect(() => {
    // If returning from Stripe payment, switch to billing tab immediately
    const billingResult = searchParams.get("billing");
    if ((billingResult === "success" || billingResult === "canceled") && isTenantAdmin) {
      setActiveTab("billing");
      return;
    }
    
    const tabParam = searchParams.get("tab");
    if (tabParam === "billing" && isTenantAdmin) {
      setActiveTab("billing");
    }
  }, [searchParams, isTenantAdmin]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="admin-panel">
      {isImpersonating && <EndSessionButton />}
      <Header 
        currentPage="admin-panel"
      />

      <EmailVerificationBanner />

      <div className="admin-content">
        <nav className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            <GroupIcon className="tab-icon" fontSize="small" />
            Users
          </button>
          {isSuperadmin && (
            <button
              className={`admin-tab ${activeTab === "tenants" ? "active" : ""}`}
              onClick={() => setActiveTab("tenants")}
            >
              <BusinessIcon className="tab-icon" fontSize="small" />
              Tenants
            </button>
          )}
          {isSuperadmin && (
            <button
              className={`admin-tab ${activeTab === "subscriptions" ? "active" : ""}`}
              onClick={() => setActiveTab("subscriptions")}
            >
              <SubscriptionsIcon className="tab-icon" fontSize="small" />
              Subscriptions
            </button>
          )}
          {/* Billing tab for tenant admins only (not superadmins) */}
          {isTenantAdmin && (
            <button
              className={`admin-tab ${activeTab === "billing" ? "active" : ""}`}
              onClick={() => setActiveTab("billing")}
            >
              <CreditCardIcon className="tab-icon" fontSize="small" />
              Billing
            </button>
          )}
          {/* Activity tab for both roles */}
          <button
            className={`admin-tab ${activeTab === "activity" ? "active" : ""}`}
            onClick={() => setActiveTab("activity")}
          >
            <TimelineIcon className="tab-icon" fontSize="small" />
            Activity
          </button>
        </nav>

        <div className="admin-tab-content">
          {activeTab === "users" && <UserManagement />}
          {activeTab === "tenants" && isSuperadmin && <TenantManagement />}
          {activeTab === "subscriptions" && isSuperadmin && <SubscriptionManagement />}
          {activeTab === "billing" && isTenantAdmin && <BillingManagement />}
          {activeTab === "activity" && <ActivityDashboard />}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPanel;
